import { z } from "zod"

export const US_STATE_CODES = [
	"AL",
	"AK",
	"AZ",
	"AR",
	"CA",
	"CO",
	"CT",
	"DE",
	"FL",
	"GA",
	"HI",
	"ID",
	"IL",
	"IN",
	"IA",
	"KS",
	"KY",
	"LA",
	"ME",
	"MD",
	"MA",
	"MI",
	"MN",
	"MS",
	"MO",
	"MT",
	"NE",
	"NV",
	"NH",
	"NJ",
	"NM",
	"NY",
	"NC",
	"ND",
	"OH",
	"OK",
	"OR",
	"PA",
	"RI",
	"SC",
	"SD",
	"TN",
	"TX",
	"UT",
	"VT",
	"VA",
	"WA",
	"WV",
	"WI",
	"WY",
] as const

export const UsStateSchema = z
	.string()
	.trim()
	.overwrite((value) => value.toUpperCase())
	.pipe(z.enum(US_STATE_CODES))
	.meta({ id: "UsState" })

export type UsState = z.infer<typeof UsStateSchema>

export const PATIENT_GENDERS = ["male", "female"] as const

export const PatientGenderSchema = z
	.string()
	.trim()
	.overwrite((value) => value.toLowerCase())
	.pipe(z.enum(PATIENT_GENDERS))
	.meta({ id: "PatientGender" })

export type PatientGender = z.infer<typeof PatientGenderSchema>

const INTAKE_DATE_OF_BIRTH_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/
export const INTAKE_MIN_DATE_OF_BIRTH_YEAR = 1900

export const IntakeDateOfBirthSchema = z
	.string()
	.trim()
	.superRefine((value, context) => {
		const match = INTAKE_DATE_OF_BIRTH_PATTERN.exec(value)
		if (match === null) {
			context.addIssue({
				code: "custom",
				message: "Date of birth must use MM-DD-YYYY format.",
			})
			return
		}

		const monthPart = match[1]
		const dayPart = match[2]
		const yearPart = match[3]
		if (monthPart === undefined || dayPart === undefined || yearPart === undefined) {
			context.addIssue({
				code: "custom",
				message: "Date of birth must be a valid calendar date.",
			})
			return
		}

		const month = Number.parseInt(monthPart, 10)
		const day = Number.parseInt(dayPart, 10)
		const year = Number.parseInt(yearPart, 10)
		if (year < INTAKE_MIN_DATE_OF_BIRTH_YEAR) {
			context.addIssue({
				code: "custom",
				message: "Date of birth must be a valid calendar date.",
			})
			return
		}
		const candidate = new Date(0)
		candidate.setUTCHours(0, 0, 0, 0)
		candidate.setUTCFullYear(year, month - 1, day)
		if (
			candidate.getUTCFullYear() !== year ||
			candidate.getUTCMonth() !== month - 1 ||
			candidate.getUTCDate() !== day
		) {
			context.addIssue({
				code: "custom",
				message: "Date of birth must be a valid calendar date.",
			})
			return
		}

		const today = new Date()
		today.setUTCHours(0, 0, 0, 0)
		if (candidate > today) {
			context.addIssue({
				code: "custom",
				message: "Date of birth cannot be in the future.",
			})
		}
	})
	.meta({ id: "IntakeDateOfBirth" })

export type IntakeDateOfBirth = z.infer<typeof IntakeDateOfBirthSchema>
